<template>
  <Modal
    ref="barcodeModalRef"
    :show-icon="false"
    :click-to-close="true"
    :modal-style="{ width: '896px' }"
    class="request-barcode-modal"
    positive-action-text="Talep Oluştur"
    negative-action-text="Vazgeç"
    :isPositiveActionButtonDisabled="isSubmitDisabled"
    @on-positive-action-click="handleSubmit"
    @on-negative-action-click="closeModal"
    @modalClosed="handleModalClosed"
  >
    <template #body>
      <div class="header">
        <p class="title">
          Barkod için Değişiklik Talep Et/Hata Bildir
        </p>
      </div>
      <div class="content">
        <!-- Bilgilendirme Mesajı -->
        <div class="info-banner">
          <Icons
            name="InfoFilledIcon"
            size="md"
            color="#4FA4E5"
          />
          <span class="info-text">
            Ürün varyantlarına ait barkodları da değiştirmek/eklemek isterseniz
            <strong>
              aşağıdaki Talep Açıklaması alanına mevcut ve yeni barkod
              numaralarını
            </strong>
            yazarak iletebilirsiniz.
          </span>
        </div>

        <div class="barcode-content">
          <!-- Mevcut Bilgiler -->
          <div class="current-information">
            <h1 class="current-information-title">
              Mevcut Bilgiler
            </h1>
            <Input
              id="product-code"
              class="product-code-input"
              label="Ürün Kodu"
              placeholder="Ürün Kodu"
              type="text"
              :rows="4"
              size="md"
              :value="modalProductCode"
              disabled
            />
            <div class="helper-text">
              Ürün kodu değişimi talep edilemez.
            </div>
            <Input
              id="barcode-code"
              label="Barkod Numarası"
              placeholder="Barkod Numarası"
              type="text"
              :rows="4"
              size="md"
              :value="modalBarcode"
              disabled
            />
          </div>

          <!-- Talep Ettiğiniz Barkod Numarası -->
          <div class="requested-information">
            <h1 class="requested-information-title">
              Talep Ettiğiniz Barkod Numarası
            </h1>
            <div class="requested-information-content">
              <Input
                id="entry-barcode-code"
                v-model:value="entryBarcodeCode"
                label="Barkod Numarası Girin"
                type="text"
                :rows="4"
                size="md"
                :is-only-number="true"
                :is-error="entryBarcodeError"
                @input="
                  entryBarcodeError = false;
                  entryBarcodeErrorMessage = '';
                "
              />
              <div
                v-if="entryBarcodeError"
                class="error-message"
              >
                {{ entryBarcodeErrorMessage }}
              </div>

              <div class="request-description-title">
                <span class="subtitle">Talep Açıklaması</span>
                <span class="optional-text">OPSİYONEL</span>
              </div>

              <Input
                id="request-description"
                v-model:value="requestDescription"
                type="textarea"
                label="Talebinizi Yazın"
                :rows="4"
                size="md"
                :style="{ height: '80px' }"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { Modal, Icons, Input } from '@n11/so-ui-components';
import { sendProductDetailEvent } from '@/utils/ga4/helper';
import { GA4_EVENT } from '@/utils/ga4/event';

const props = defineProps({
  selectedRowData: {
    type: Object,
    default: null,
  },
  selectedGtin: {
    type: String,
    default: null,
  },
  productDetailTableRef: {
    type: Object,
    default: null,
  },
});

const store = useStore();
const productDetail = computed(() => store.getters['products/getProductDetail']);
const barcodeModalRef = ref(null);
const entryBarcodeCode = ref('');
const entryBarcodeError = ref(false);
const entryBarcodeErrorMessage = ref('');
const requestDescription = ref('');
const skipDismissOnClose = ref(false);

const modalProductCode = computed(() => props.selectedRowData?.id || '-');
const modalBarcode = computed(
  () => props.selectedGtin || props.selectedRowData?.gtin || '-',
);

const isSubmitDisabled = computed(() => {
  return !entryBarcodeCode.value || entryBarcodeCode.value.trim() === '';
});

const handleSubmit = async () => {
  const barcodeValue = String(entryBarcodeCode.value || '').trim();

  if (barcodeValue.length > 14) {
    entryBarcodeError.value = true;
    entryBarcodeErrorMessage.value =
      'Barkod numarası en fazla 13 rakamdan oluşabilir.';
    return;
  }

  entryBarcodeError.value = false;
  entryBarcodeErrorMessage.value = '';

  const groupId = productDetail.value?.groupId;

  try {
    const payload = {
      groupId: groupId,
      barcode: barcodeValue,
      description: requestDescription.value || '',
    };

    await store.dispatch('products/postProductFeedbackSuggestionsUsc', payload);
    sendProductDetailEvent(GA4_EVENT.REQUEST_UPDATE_REPORT_BUG_SUCCESS, {
      button_name: 'Talep Oluştur',
      button_type: 'Request Success',
      button_area: 'Order and Variant Information / Barcode',
      item_id: productDetail.value?.id,
    });

    store.commit('common/SET_TOASTER_DATA', {
      type: 'success',
      title: 'Başarılı',
      description: 'Talep başarıyla oluşturuldu',
    });

    skipDismissOnClose.value = true;
    if (barcodeModalRef.value) {
      barcodeModalRef.value.hiddenModal();
    }
  } catch (error) {
    const errorMessage = error?.data?.errors?.[0]?.message;

    if (errorMessage) {
      store.commit('common/SET_TOASTER_DATA', {
        type: 'error',
        title: 'Hata',
        description: errorMessage,
      });
    }
  }
};

const openModal = () => {
  if (barcodeModalRef.value) {
    barcodeModalRef.value.openModal();
  }
};

const closeModal = () => {
  if (barcodeModalRef.value) {
    barcodeModalRef.value.hiddenModal();
  }
};

const handleModalClosed = () => {
  if (!skipDismissOnClose.value) {
    sendProductDetailEvent(GA4_EVENT.REQUEST_UPDATE_REPORT_BUG_DISMISS, {
      button_name: 'Vazgeç',
      button_type: 'Request Dismiss',
      button_area: 'Order and Variant Information / Barcode',
      item_id: productDetail.value?.id,
    });
  }
  skipDismissOnClose.value = false;
};

defineExpose({
  openModal,
  closeModal,
});
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables';

.header {
  margin-bottom: 24px;

  .title {
    color: $colorN80;
    font-size: 20px;
    font-weight: 700;
    letter-spacing: -0.2px;
    line-height: 26px;
    text-align: left;
  }
}

.content {
  flex: 1;
  padding-right: 4px;
  overflow: hidden auto;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    border-radius: 3px;
    background: $colorN10;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 3px;
    background: $colorN40;

    &:hover {
      background: $colorN50;
    }
  }
}

.info-banner {
  display: flex;
  align-items: flex-start;
  padding: 16px;
  border-radius: 6px;
  margin-bottom: 12px;
  background-color: $colorB05;
  gap: 12px;

  .info-text {
    color: $colorN80;
    font-size: 13px;
    letter-spacing: -0.05px;
    line-height: 18px;
    text-align: left;

    strong {
      font-weight: 700;
    }
  }
}

.barcode-content {
  display: grid;
  gap: 50px;
  grid-template-columns: repeat(2, 1fr);

  .requested-information-content {
    padding: 16px;
    border-radius: 8px;
    background-color: $colorBe20;
  }

  .requested-information-title {
    margin-bottom: 16px;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: -0.05px;
    line-height: 20px;
    text-align: left;
  }

  .request-description-title {
    margin: 12px 0 8px;
  }

  .subtitle {
    font-size: 14px;
    font-weight: 600;
    letter-spacing: -0.05px;
    line-height: 18px;
  }

  .helper-text {
    padding: 4px 12px 0;
    margin-bottom: 12px;
    color: $colorN50;
    font-size: 11px;
    line-height: 14px;
    text-align: left;
  }

  .optional-text {
    padding: 0 8px;
    border: 0.5px solid $colorN30;
    border-radius: 3px;
    margin-left: 8px;
    background-color: $colorN00;
    color: $colorN60;
    font-size: 10px;
    font-weight: 700;
    line-height: normal;
  }

  .current-information-title {
    margin-bottom: 32px;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: -0.05px;
    line-height: 20px;
    text-align: left;
  }

  .requested-information {
    position: relative;
    text-align: left;

    &::after {
      position: absolute;
      top: 0;
      left: -25px;
      width: 0.5px;
      height: 100%;
      background-color: $colorN30;
      content: '';
    }
  }

  .error-message {
    margin-top: 4px;
    margin-left: 12px;
    color: $colorE50;
    font-size: 12px;
    line-height: 16px;
    text-align: left;
  }
}

.request-barcode-modal {
  :deep(.modal-container) {
    display: flex;
    flex-direction: column;
    padding: 20px;
  }

  :deep(.modal-header) {
    position: absolute;
    top: 7px;
    right: 4px;
  }

  :deep(.modal-body) {
    overflow: hidden !important;
    height: 100%;
    padding: 0 !important;

    .content {
      display: flex;
      overflow: hidden !important;
      flex: 1;
      flex-direction: column;
      gap: 0 !important;
    }
  }

  :deep(.modal-footer) {
    margin-top: 24px !important;
    padding: 0 !important;
  }
}
</style>
